import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, subDays, format, isToday } from 'date-fns';
import { it as itLocale, enUS } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { Button } from '../ui/button';
import { ScheduleDayPickerSheet } from './ScheduleDayPickerSheet';

interface ScheduleDayNavigatorProps {
  date: Date;
  onDateChange: (date: Date) => void;
}

export function ScheduleDayNavigator({ date, onDateChange }: ScheduleDayNavigatorProps) {
  const { t, i18n } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);

  const dateFnsLocale = i18n.language?.startsWith('it') ? itLocale : enUS;
  const dayLabel = format(date, 'EEEE d MMM', { locale: dateFnsLocale });
  const label = isToday(date) ? `${t('schedule.today')} · ${dayLabel}` : dayLabel;

  return (
    <>
      <div className="sticky top-16 z-30 -mx-4 px-4 sm:-mx-6 sm:px-6 bg-background border-b flex items-center justify-between gap-2 py-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDateChange(subDays(date, 1))}
          aria-label={t('schedule.prevDay')}
        >
          <ChevronLeft className="size-5" />
        </Button>

        <button
          type="button"
          className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-accent transition-colors"
          onClick={() => setPickerOpen(true)}
          aria-label={t('schedule.pickDay')}
        >
          <span className="text-sm font-medium capitalize">{label}</span>
          <CalendarDays className="size-4 text-muted-foreground" />
        </button>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDateChange(addDays(date, 1))}
          aria-label={t('schedule.nextDay')}
        >
          <ChevronRight className="size-5" />
        </Button>
      </div>

      <ScheduleDayPickerSheet
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        selected={date}
        onDaySelect={onDateChange}
      />
    </>
  );
}
